import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { dayjs, metersToMiles } from '../lib/utils';
import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { useCreateRaceRequest } from './mutations/useCreateRaceRequest';

export const useAccessToken = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryResults = useQuery({
    queryKey: ['accessToken'],
    queryFn: async () => {
      return await getAccessTokenSilently({ cacheMode: 'on' });
    },
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error, query) {
      console.error('Failed to fetch access token:', error);
      throw error;
    },
  });
  return queryResults;
};

export const useProfile = () => {
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const queryResults = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      if (!accessToken.data) {
        navigate({
          to: '/login',
          replace: true,
        });
        return null;
      }
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );
      if (response.status !== 200) {
        return null;
      }
      const data = await response.json();
      return data;
    },
    enabled: !!accessToken.data, // Only fetch if the access token is available
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error, query) {
      console.error('Failed to fetch profile:', error);
      throw error;
    },
  });

  return queryResults;
};

export type CompletedWorkout = {
  id: number;
  activityId: string;
  connectionId: string;
  connections?: { connectionType: 'strava' | 'garmin'; connectionId: string }[];
  title: string;
  description: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  model: string;
  type: string;
  distance: number;
  movingTime: number;
  elapsedTime: number;
  averageSpeed: number;
  maxSpeed: number;
  maxHeartRate: number;
  averageHeartRate: number;
  completedAt: number;
  userId: number;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  polylineEncoded?: string;
  elevationGain?: number;
  elevationLow?: number;
  elevationHigh?: number;
};

const DEFAULT_WORKOUT_LIMIT = 100;

export const useWorkouts = ({
  // default to the first day of the year
  startDate = dayjs().startOf('year').toDate(),
  // default to today
  endDate = dayjs().endOf('day').toDate(),
  limit = DEFAULT_WORKOUT_LIMIT,
}: {
  startDate?: Date;
  endDate?: Date;
  limit?: number;
} = {}): UseQueryResult<{
  workouts: CompletedWorkout[];
} | null> => {
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const queryResults = useQuery({
    queryKey: ['workouts', startDate, endDate, limit],
    queryFn: async () => {
      if (!accessToken.data) {
        navigate({
          to: '/login',
          replace: true,
        });
        return null;
      }
      // add params to the url if they are provided
      const url = new URL(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/workouts`,
      );

      if (startDate) {
        url.searchParams.set(
          'startDate',
          startDate.toISOString().split('T')[0],
        );
      }
      if (endDate) {
        url.searchParams.set('endDate', endDate.toISOString().split('T')[0]);
      }
      if (limit) {
        url.searchParams.set('limit', limit.toString());
      }
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken.data}`,
        },
      });
      if (response.status !== 200) {
        return null;
      }
      const { workouts } = await response.json();
      const sortedWorkouts: CompletedWorkout[] = workouts.sort(
        (a: CompletedWorkout, b: CompletedWorkout) =>
          new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime(),
      );
      return {
        workouts: sortedWorkouts,
      };
    },
    enabled: !!accessToken.data, // Only fetch if the access token is available
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error, query) {
      console.error('Failed to fetch workouts:', error);
      throw error;
    },
  });
  return queryResults;
};

export const useWeeklyStats = (): {
  isLoading: boolean;
  weeklyStats: {
    totalTime: number;
    totalDistance: number;
    totalElevation: number;
    avgPace: number;
    workoutCount: number;
    recoveryCount: number;
  };
} => {
  const { startOfWeek, endOfWeek } = useMemo(() => {
    return {
      startOfWeek: dayjs().startOf('week').startOf('day').toDate(),
      endOfWeek: dayjs().endOf('week').endOf('day').toDate(),
    };
  }, []);
  const { data: workouts, isLoading } = useWorkouts({
    startDate: startOfWeek,
    endDate: endOfWeek,
    limit: 20,
  });

  const {
    totalTime,
    totalDistance,
    totalElevation,
    avgPace,
    workoutCount,
    recoveryCount,
  } = useMemo(() => {
    return {
      totalTime: workouts?.workouts.reduce(
        (acc, workout) => acc + (workout.elapsedTime || 0),
        0,
      ),
      totalDistance: workouts?.workouts
        .filter((workout) => workout.type === 'run')
        .filter((workout) => workout.distance && workout.distance > 0)
        .reduce((acc, workout) => acc + (workout.distance || 0), 0),
      totalElevation: workouts?.workouts
        .filter((workout) => workout.type === 'run')
        .reduce((acc, workout) => acc + (workout.elevationGain || 0), 0),
      avgPace:
        (workouts?.workouts
          .filter(
            (workout) =>
              workout.type === 'run' &&
              workout.elapsedTime &&
              workout.elapsedTime > 0,
          )
          .reduce((acc, workout) => acc + workout.elapsedTime / 60, 0) || 0) /
        (workouts?.workouts
          .filter(
            (workout) =>
              workout.type === 'run' &&
              workout.distance &&
              workout.distance > 0,
          )
          .reduce((acc, workout) => acc + metersToMiles(workout.distance), 0) ||
          0),
      workoutCount: workouts?.workouts.filter((w) => w.type !== 'recovery')
        .length,
      recoveryCount: workouts?.workouts.filter((w) => w.type === 'recovery')
        .length,
    };
  }, [workouts]);
  return {
    isLoading,
    weeklyStats: {
      totalTime: totalTime || 0,
      totalDistance: totalDistance || 0,
      totalElevation: totalElevation || 0,
      avgPace: avgPace || 0,
      workoutCount: workoutCount || 0,
      recoveryCount: recoveryCount || 0,
    },
  };
};

export const useWeeklyStatsComparison = (
  weekOffset: number = 0,
): {
  isLoading: boolean;
  weeklyStats: {
    totalTime: number;
    totalDistance: number;
    totalElevation: number;
    avgPace: number;
    workoutCount: number;
    recoveryCount: number;
  };
  lastWeekStats: {
    totalTime: number;
    totalDistance: number;
    totalElevation: number;
    avgPace: number;
    workoutCount: number;
    recoveryCount: number;
  };
  percentageChanges: {
    totalTime: number | null;
    totalDistance: number | null;
    totalElevation: number | null;
    avgPace: number | null;
    workoutCount: number | null;
    recoveryCount: number | null;
  };
  selectedWeekStart: Date;
  selectedWeekEnd: Date;
  isCurrentWeek: boolean;
} => {
  const {
    thisWeekStartDate,
    thisWeekEndDate,
    lastWeekStartDate,
    lastWeekEndDate,
    isCurrentWeek,
  } = useMemo(() => {
    const now = dayjs();
    const selectedWeekStart = now
      .add(weekOffset, 'week')
      .startOf('week')
      .startOf('day');
    const selectedWeekEnd =
      weekOffset === 0
        ? now.endOf('day') // For current week, go up to today
        : now.add(weekOffset, 'week').endOf('week').endOf('day'); // For past weeks, include the whole week

    // For comparison - get the same period from the previous week
    const comparisonWeekStart = selectedWeekStart.subtract(1, 'week');
    const comparisonWeekEnd =
      weekOffset === 0
        ? now.subtract(1, 'week').endOf('day') // For current week, compare same day last week
        : selectedWeekEnd.subtract(1, 'week'); // For past weeks, compare whole week

    return {
      thisWeekStartDate: selectedWeekStart.toDate(),
      thisWeekEndDate: selectedWeekEnd.toDate(),
      lastWeekStartDate: comparisonWeekStart.toDate(),
      lastWeekEndDate: comparisonWeekEnd.toDate(),
      isCurrentWeek: weekOffset === 0,
    };
  }, [weekOffset]);

  const { data: thisWeekWorkouts, isLoading: isLoadingThisWeek } = useWorkouts({
    startDate: thisWeekStartDate,
    endDate: thisWeekEndDate,
    limit: 50,
  });

  const { data: lastWeekWorkouts, isLoading: isLoadingLastWeek } = useWorkouts({
    startDate: lastWeekStartDate,
    endDate: lastWeekEndDate,
    limit: 50,
  });

  const calculateStats = (workouts: CompletedWorkout[] | undefined) => {
    if (!workouts) {
      return {
        totalTime: 0,
        totalDistance: 0,
        totalElevation: 0,
        avgPace: 0,
        workoutCount: 0,
        recoveryCount: 0,
      };
    }

    const totalTime = workouts.reduce(
      (acc, workout) => acc + (workout.elapsedTime || 0),
      0,
    );

    const totalDistance = workouts
      .filter((workout) => workout.type === 'run')
      .filter((workout) => workout.distance && workout.distance > 0)
      .reduce((acc, workout) => acc + (workout.distance || 0), 0);

    const totalElevation = workouts
      .filter((workout) => workout.type === 'run')
      .reduce((acc, workout) => acc + (workout.elevationGain || 0), 0);

    const totalRunTime = workouts
      .filter(
        (workout) =>
          workout.type === 'run' &&
          workout.elapsedTime &&
          workout.elapsedTime > 0,
      )
      .reduce((acc, workout) => acc + workout.elapsedTime / 60, 0);

    const totalRunDistance = workouts
      .filter(
        (workout) =>
          workout.type === 'run' && workout.distance && workout.distance > 0,
      )
      .reduce((acc, workout) => acc + metersToMiles(workout.distance), 0);

    const avgPace = totalRunDistance > 0 ? totalRunTime / totalRunDistance : 0;
    const workoutCount = workouts.filter((w) => w.type !== 'recovery').length;
    const recoveryCount = workouts.filter((w) => w.type === 'recovery').length;

    return {
      totalTime,
      totalDistance,
      totalElevation,
      avgPace,
      workoutCount,
      recoveryCount,
    };
  };

  const thisWeekStats = useMemo(
    () => calculateStats(thisWeekWorkouts?.workouts),
    [thisWeekWorkouts],
  );
  const lastWeekStats = useMemo(
    () => calculateStats(lastWeekWorkouts?.workouts),
    [lastWeekWorkouts],
  );

  const percentageChanges = useMemo(() => {
    const calculatePercentageChange = (
      current: number,
      previous: number,
    ): number | null => {
      if (previous === 0) {
        return current > 0 ? 100 : null; // If previous was 0 and current > 0, it's a 100% increase
      }
      return ((current - previous) / previous) * 100;
    };

    return {
      totalTime: calculatePercentageChange(
        thisWeekStats.totalTime,
        lastWeekStats.totalTime,
      ),
      totalDistance: calculatePercentageChange(
        thisWeekStats.totalDistance,
        lastWeekStats.totalDistance,
      ),
      totalElevation: calculatePercentageChange(
        thisWeekStats.totalElevation,
        lastWeekStats.totalElevation,
      ),
      avgPace: calculatePercentageChange(
        thisWeekStats.avgPace,
        lastWeekStats.avgPace,
      ),
      workoutCount: calculatePercentageChange(
        thisWeekStats.workoutCount,
        lastWeekStats.workoutCount,
      ),
      recoveryCount: calculatePercentageChange(
        thisWeekStats.recoveryCount,
        lastWeekStats.recoveryCount,
      ),
    };
  }, [thisWeekStats, lastWeekStats]);

  return {
    isLoading: isLoadingThisWeek || isLoadingLastWeek,
    weeklyStats: thisWeekStats,
    lastWeekStats,
    percentageChanges,
    selectedWeekStart: thisWeekStartDate,
    selectedWeekEnd: thisWeekEndDate,
    isCurrentWeek,
  };
};

export const useCurrentStreak = () => {
  const today = dayjs().endOf('day').toDate();
  const { data: workouts } = useWorkouts({
    startDate: dayjs().startOf('year').toDate(),
    endDate: today,
  });

  // sort the workouts in descending order
  const sortedWorkouts = workouts?.workouts.sort(
    (a, b) =>
      new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime(),
  );

  const lastDayWithoutWorkout = useMemo(() => {
    // go back to beginning of the year, and check if there is a workout on that day
    const today = dayjs();
    const startOfYear = today.startOf('year');
    const diffInDays = today.diff(startOfYear, 'day');
    let totalNumberOfConsecutiveDays = 0;
    for (let i = 0; i <= diffInDays; i++) {
      const dayOfWorkout = today.subtract(i, 'day');
      const anyWorkoutOnThisDay = sortedWorkouts?.find((workout) => {
        // Convert UTC workout timestamp to local date for comparison
        const workoutLocalDate = dayjs(workout.completedAt);
        return workoutLocalDate.isSame(dayOfWorkout, 'day');
      });
      if (anyWorkoutOnThisDay) {
        // workout on this day, set the oldest day with a workout and increment the total number of consecutive days
        totalNumberOfConsecutiveDays++;
        continue;
      }
      // no workout on this day, break
      break;
    }

    return totalNumberOfConsecutiveDays;
  }, [sortedWorkouts]);
  return lastDayWithoutWorkout;
};

export const useRacePlan = (): UseQueryResult<{
  id: number;
  summary: string;
  goal_time: string;
  created_at: string;
  race: {
    id: number;
    name: string;
    distance: string;
    date: number;
    gpx_file_url: string;
    created_at: string;
  };
  training_plan: {
    week: number;
    workouts: {
      id: number;
      planned_date: string;
      type: string;
      distance: string;
      pace_goal: string;
      total_time: string;
      intervals: string;
      details: string;
      intention: string;
      completed: boolean;
      activity_id: number | undefined;
    }[];
  }[];
} | null> => {
  const accessToken = useAccessToken();
  const navigate = useNavigate();
  const queryResults = useQuery({
    queryKey: ['racePlan'],
    queryFn: async () => {
      if (!accessToken.data) {
        navigate({
          to: '/login',
          replace: true,
        });
        return null;
      }
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/race`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );
      if (response.status !== 200) {
        return null;
      }
      const { plan } = await response.json();
      return plan;
    },
    enabled: !!accessToken.data, // Only fetch if the access token is available
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error, query) {
      console.error('Failed to fetch race plan:', error);
      throw error;
    },
  });
  return queryResults;
};

export const useGPXData = ({ url }: { url: string }) => {
  const queryResults = useQuery({
    queryKey: ['gpx', url],
    queryFn: async () => {
      const response = await fetch(url);
      return response.text();
    },
    enabled: !!url,
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error, query) {
      console.error('Failed to fetch gpx data:', error);
      throw error;
    },
  });
  return queryResults;
};

export const useRaces = ({
  startDate = new Date(new Date().setHours(0, 0, 0, 0)),
  endDate,
  limit = 100,
  offset = 0,
}: {
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  offset?: number;
} = {}) => {
  const accessToken = useAccessToken();
  const queryResults = useQuery({
    queryKey: ['races'],
    queryFn: async () => {
      const url = new URL(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races`,
      );
      if (startDate) {
        url.searchParams.set(
          'startDate',
          startDate.toISOString().split('T')[0],
        );
      }
      if (endDate) {
        url.searchParams.set('endDate', endDate.toISOString().split('T')[0]);
      }
      if (limit) {
        url.searchParams.set('limit', limit.toString());
      }
      if (offset) {
        url.searchParams.set('offset', offset.toString());
      }
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken.data}`,
        },
      });
      if (response.status !== 200) {
        return null;
      }
      const data = await response.json();
      return data.races || [];
    },
    enabled: !!accessToken.data,
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error) {
      console.error('Failed to fetch races:', error);
      throw error;
    },
  });
  return queryResults;
};

export const useRace = (id?: string) => {
  const accessToken = useAccessToken();
  return useQuery({
    queryKey: ['race', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/races/${id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );
      if (response.status !== 200) {
        return null;
      }
      const data = await response.json();
      return data.race || null;
    },
    enabled: !!accessToken.data && !!id,
    staleTime: 15 * 60 * 1000,
    refetchOnMount: true,
    throwOnError(error) {
      console.error('Failed to fetch race:', error);
      throw error;
    },
  });
};

export const useCreateRace = () => {
  const accessToken = useAccessToken();
  const createRaceMutation = useCreateRaceRequest(accessToken.data);

  return { createRace: createRaceMutation.mutateAsync, createRaceMutation };
};

export type PlannedWorkout = {
  id: number;
  activity_id: number;
  completed: boolean;
  created_at: string;
  details: string;
  distance: string;
  goal_time: string;
  intention: string;
  intervals: string;
  planned_date: string;
  race_id: number;
  summary: string;
  total_time: string;
  type: string;
  updated_at: string;
  user_feedback: string;
  user_id: number;
  user_liked: number;
  week: number;
};

export const usePlannedWorkouts = ({
  startDate,
  endDate,
  limit,
}: {
  startDate?: Date;
  endDate?: Date;
  limit?: number;
}): UseQueryResult<PlannedWorkout[]> => {
  const accessToken = useAccessToken();
  // Serialize dates to YYYY-MM-DD strings for the query key. Passing raw Date
  // objects produces a new key on every render (JSON.stringify includes ms),
  // which causes React Query to refetch infinitely.
  const startDateKey = startDate
    ? startDate.toISOString().split('T')[0]
    : undefined;
  const endDateKey = endDate
    ? new Date(endDate).toISOString().split('T')[0]
    : undefined;
  const queryResults = useQuery({
    queryKey: ['plannedWorkouts', startDateKey, endDateKey, limit],
    queryFn: async () => {
      const url = new URL(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts`,
      );
      if (startDate) {
        url.searchParams.set(
          'startDate',
          startDate.toISOString().split('T')[0],
        );
      }
      if (endDate) {
        url.searchParams.set(
          'endDate',
          new Date(endDate).toISOString().split('T')[0],
        );
      }
      if (limit) {
        url.searchParams.set('limit', limit.toString());
      }
      const response = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${accessToken.data}`,
        },
      });
      if (response.status !== 200) {
        return null;
      }
      const data = await response.json();
      return data.plannedWorkouts;
    },
    enabled: !!accessToken.data,
    staleTime: 15 * 60 * 1000, // Consider data fresh for 15 minutes
    refetchOnMount: true,
    throwOnError(error) {
      console.error('Failed to fetch planned workouts:', error);
      throw error;
    },
  });
  return queryResults;
};

export const usePlannedWorkout = (
  id: string | number,
): UseQueryResult<PlannedWorkout> => {
  const accessToken = useAccessToken();
  return useQuery({
    queryKey: ['plannedWorkout', id],
    queryFn: async () => {
      const response = await fetch(
        `${import.meta.env.REACT_APP_API_SERVICE_URI}/api/profile/plan/workouts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken.data}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error('Failed to fetch planned workout');
      }
      return response.json();
    },
    enabled: !!accessToken.data && !!id,
  });
};
